import { Menu as ArkMenu, Portal as ArkPortal } from "@ark-ui/react";
import type { ReactElement, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Link, type To } from "react-router";
import { twJoin } from "tailwind-merge";

import chevronRightIcon from "@/assets/icons/chevron-right.svg";
import dotsVerticalIcon from "@/assets/icons/dots-vertical.svg";
import { buttonVariants } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { twResolve } from "@/helpers/tw-resolve";

type ContextMenuItemLabels = {
  default: string;
  disabled?: string;
};

export type ContextMenuItem = {
  labels: ContextMenuItemLabels;
  value: string;
  icon?: ReactElement;
  "data-testid"?: string;
  isHidden?: boolean;
  subItems?: ContextMenuItem[];
  isDisabled?: boolean;
} & (
  | {
      type?: "button";
      href?: never;
      callbackFn: () => void | Promise<void>;
      subItems?: never;
    }
  | {
      type: "link";
      href: To;
      callbackFn?: () => void | Promise<void>;
      subItems?: never;
    }
  | {
      type: "trigger";
      href?: never;
      callbackFn?: never;
      subItems: ContextMenuItem[];
    }
);

export type ContextMenuProps = {
  items: ContextMenuItem[];
  placement?: "top" | "bottom";
  triggerComponent?: ReactNode;
  triggerLabel?: string;
  onOpenComplete?: () => void;
};

function visibleItems(items: ContextMenuItem[]): ContextMenuItem[] {
  return items.filter((item) => {
    if (item.isHidden) {
      return false;
    }

    if (item.subItems) {
      return visibleItems(item.subItems).length > 0;
    }

    return true;
  });
}

export function ContextMenu({
  items,
  triggerComponent,
  triggerLabel,
  placement = "bottom",
  onOpenComplete,
}: ContextMenuProps): ReactNode {
  const [isOpened, setIsOpened] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const filteredItems = visibleItems(items);

  useEffect(() => {
    if (!isOpened) {
      return;
    }

    // Track browser scrolling from the moment the menu opens. Scroll events queued
    // while bringing the trigger into view must not dismiss the new menu.
    const scrollPositions = new Map<EventTarget, { element: Element; left: number; top: number }>();
    let element: Element | null = triggerRef.current;
    while (element) {
      scrollPositions.set(element, { element, left: element.scrollLeft, top: element.scrollTop });
      element = element.parentElement;
    }

    const scrollingElement = document.scrollingElement;
    if (scrollingElement) {
      scrollPositions.set(document, {
        element: scrollingElement,
        left: scrollingElement.scrollLeft,
        top: scrollingElement.scrollTop,
      });
    }

    const handleScroll = (event: Event) => {
      const position = event.target ? scrollPositions.get(event.target) : undefined;
      if (position && (position.element.scrollLeft !== position.left || position.element.scrollTop !== position.top)) {
        setIsOpened(false);
      }
    };
    document.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpened]);

  if (filteredItems.length === 0) {
    return null;
  }

  return (
    <ArkMenu.Root
      positioning={{
        placement: placement === "bottom" ? "bottom-end" : "top-end",
      }}
      lazyMount
      unmountOnExit
      open={isOpened}
      onOpenChange={({ open }) => {
        setIsOpened(open);
        if (open) {
          onOpenComplete?.();
        }
      }}
    >
      <span
        ref={triggerRef}
        className="contents"
        onClick={(event) => {
          // Let Ark handle the trigger first, then keep the click away from an interactive parent.
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <ArkMenu.Trigger asChild>
          {triggerComponent ?? (
            <button
              type="button"
              aria-label={triggerLabel}
              data-testid="context-menu-trigger-btn"
              className={twResolve(
                buttonVariants({ variant: "ghostSecondary", size: "sm", type: "button" }),
                "flex context-menu-trigger items-center justify-center focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-inset",
              )}
            >
              <MenuIcon name={dotsVerticalIcon} />
            </button>
          )}
        </ArkMenu.Trigger>
      </span>
      <ArkPortal>
        <ArkMenu.Positioner className="z-50!">
          <MenuContent>
            {filteredItems.map((item) => {
              if (item.subItems && item.subItems.length > 0) {
                return (
                  <MenuRoot key={item.value}>
                    <MenuItem item={item} />
                    <ArkPortal>
                      <ArkMenu.Positioner className="z-50!">
                        <MenuContent>
                          {visibleItems(item.subItems).map((subItem) => (
                            <MenuItem key={subItem.value} item={subItem} />
                          ))}
                        </MenuContent>
                      </ArkMenu.Positioner>
                    </ArkPortal>
                  </MenuRoot>
                );
              }

              return <MenuItem item={item} key={item.value} />;
            })}
          </MenuContent>
        </ArkMenu.Positioner>
      </ArkPortal>
    </ArkMenu.Root>
  );
}

function MenuContent({ children }: { children: ReactNode }): ReactNode {
  return (
    <ArkMenu.Content
      data-testid="context-menu-action-list"
      className={twJoin(
        "context-menu-content overflow-hidden rounded-md border border-border-default bg-background-default text-text-primary focus:outline-hidden",
        "repeat-1 data-[placement=left-end]:origin-bottom-right data-[placement=left-start]:origin-top-right data-[placement=right-end]:origin-bottom-left data-[placement=right-start]:origin-top-left",
        "context-menu-enter-offset data-[state=open]:animate-in data-[state=open]:fade-in",
      )}
    >
      {children}
    </ArkMenu.Content>
  );
}

function MenuItem({ item }: { item: ContextMenuItem }): ReactNode {
  const label = item.isDisabled && item.labels.disabled ? item.labels.disabled : item.labels.default;
  const hasSubItems = item.subItems && item.subItems.length > 0;
  const ItemComponent = hasSubItems ? ArkMenu.TriggerItem : ArkMenu.Item;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (item.isDisabled) {
      return;
    }

    // Selecting an item closes the menu, which restores focus to the trigger and
    // tears down its dismiss layer. Running the action synchronously would open a
    // dialog/modal into that teardown, and it gets dismissed on the same tick.
    // Defer to the next frame so the menu is fully closed before the action runs.
    const { callbackFn } = item;
    if (callbackFn) {
      requestAnimationFrame(() => {
        void callbackFn();
      });
    }
  };

  return (
    <ItemComponent
      value={item.value}
      disabled={item.isDisabled}
      data-testid={item["data-testid"]}
      onClick={handleClick}
      // oxlint-disable-next-line tailwindcss/no-conflicting-classes -- TODO: transition-colors overrides context-menu-item's transition-duration
      className="flex w-full cursor-pointer items-center gap-sm bg-background-default context-menu-disabled-icon context-menu-item text-left transition-colors hover:bg-background-secondary data-disabled:cursor-default data-disabled:text-text-disabled data-highlighted:bg-background-secondary"
      asChild={item.type === "link"}
    >
      {item.type === "link" ? (
        <Link to={item.href}>
          {item.icon}
          <span className="text-body">{label}</span>
        </Link>
      ) : (
        <button type="button" className="flex w-full items-center justify-between gap-md focus:outline-hidden">
          <Inline as="span" gap="sm" align="center">
            {item.icon}
            <span className="text-body">{label}</span>
          </Inline>
          {item.type === "trigger" && <MenuIcon name={chevronRightIcon} tone="secondary" />}
        </button>
      )}
    </ItemComponent>
  );
}

function MenuRoot({ children }: { children: ReactNode }): ReactNode {
  return (
    <ArkMenu.Root positioning={{ placement: "right-start" }} lazyMount unmountOnExit>
      {children}
    </ArkMenu.Root>
  );
}

function MenuIcon({ name, tone }: { name: string; tone?: "secondary" }): ReactNode {
  return (
    <span className={twResolve("shrink-0 fill-transparent", tone === "secondary" && "text-text-secondary")}>
      <svg width={16} height={16} aria-hidden="true">
        <use xlinkHref={`#${name}`} />
      </svg>
    </span>
  );
}
